import { parseZodErrors, uploadPhotoSchema } from '@utils';
import { ZodError } from 'zod';
import { ErrorMessages } from '@types';
import * as Sentry from '@sentry/react';
import React from 'react';
import { FileObject } from '@hooks';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

interface Props {
  mutateImageUpload: UseMutateAsyncFunction<
    { file: File; blob_endpoint: string; blob_uri: string },
    Error,
    File,
    unknown
  >;
  uploadedFiles: FileObject[];
  uploadedImagesBlob: {
    blob_uri: string;
    blob_endpoint: string;
    filename: string;
  }[];
  navigate: NavigateFunction;
  navigatePath: string;
  errorPath: string;
  setErrorMessages: React.Dispatch<React.SetStateAction<ErrorMessages[]>>;
  updateUploadedImagesBlobs: (
    uploadedImagesBlob: {
      blob_uri: string;
      blob_endpoint: string;
      filename: string;
    }[],
  ) => void;
}

export const handleUploadFile = async ({
  uploadedFiles,
  uploadedImagesBlob,
  navigate,
  navigatePath,
  mutateImageUpload,
  errorPath,
  setErrorMessages,
  updateUploadedImagesBlobs,
}: Props) => {
  try {
    uploadPhotoSchema.parse({ uploadedFiles });

    if (uploadedFiles.length === 0) {
      navigate(navigatePath);
      return;
    }
    let updatedImagesBlobsArray: typeof uploadedImagesBlob =
      uploadedImagesBlob.filter(
        (item) => !!item.blob_endpoint && !!item.blob_uri,
      );
    for (const fileObject of uploadedFiles) {
      const { file, blob_endpoint, blob_uri } = fileObject;
      if (!blob_endpoint || !blob_uri) {
        await mutateImageUpload(file, {
          onSuccess: (data) => {
            const updatedFileObject = {
              filename: file.name,
              blob_endpoint: data.blob_endpoint,
              blob_uri: data.blob_uri,
            };
            updatedImagesBlobsArray.push(updatedFileObject);
          },
          onError: (error) => {
            throw error;
          },
        });
      }
    }
    updateUploadedImagesBlobs(updatedImagesBlobsArray);
    navigate(navigatePath);
  } catch (error) {
    if (error instanceof ZodError) {
      const validationErrors = parseZodErrors(error);
      setErrorMessages(
        validationErrors.map((error: ErrorMessages) => ({
          ...error,
          path: errorPath,
        })),
      );
    } else {
      Sentry.captureException(error);
      setErrorMessages([
        { path: errorPath, message: (error as Error).message },
      ]);
    }
  }
};
